/**
 * ImpactOrchestration.jsx — GRANULLO_IMPACT_PANEL_V1
 *
 * CSR / Donations impact storytelling surface.
 * Replaces mechanical release-orchestration panel with a calm, human narrative.
 *
 * Principles: Attio composure, Notion clarity, editorial restraint.
 * Max 2 background tones, 1 scarlet accent, typography-first hierarchy.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SCARLET = '#CD2200';

/* ═══ IMPACT STORIES (rotating) ═══ */
const STORIES = [
  {
    flag: '🇮🇳',
    place: 'Pune, India',
    quote: 'I can now focus in class without worrying about food.',
    name: 'Ananya',
    age: 9,
    context: 'A meal reached a student',
    initials: 'A',
    tone: '#E8C88C',
  },
  {
    flag: '🇰🇪',
    place: 'Nakuru, Kenya',
    quote: 'My textbooks arrived before the term started.',
    name: 'Kofi',
    age: 12,
    context: 'School supplies delivered',
    initials: 'K',
    tone: '#C9A87C',
  },
  {
    flag: '🇵🇭',
    place: 'Cebu, Philippines',
    quote: 'The clinic gave me medicine for free, because of the card.',
    name: 'Liana',
    age: 7,
    context: 'Pharmacy benefit received',
    initials: 'L',
    tone: '#D4B896',
  },
];

/* ═══ AGGREGATE METRICS ═══ */
const METRICS = [
  { value: 12840, label: 'Lives supported', context: 'direct verified support', suffix: '' },
  { value: 48200, label: 'Meals delivered', context: 'no intermediary leakage', suffix: '' },
  { value: 2.1, label: 'CO\u2082 avoided', context: 'measured, not estimated', suffix: 't', decimals: 1 },
];

/* ═══ FLOW STEPS ═══ */
const FLOW = [
  { label: 'Received', done: true },
  { label: 'Verified', done: true },
  { label: 'Disbursed', done: true },
  { label: 'Impact created', done: true, active: true },
];

/* ═══ COUNT-UP HOOK ═══ */
function useCountUp(target, { duration = 1400, decimals = 0, start = false } = {}) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  const display = decimals > 0
    ? val.toFixed(decimals)
    : Math.round(val).toLocaleString();
  return display;
}

function MetricItem({ value, label, context, suffix = '', decimals = 0, start }) {
  const display = useCountUp(value, { decimals, start });
  return (
    <div
      className="impact-metric"
      data-testid={`impact-metric-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      <div className="impact-metric-value">
        {display}
        {suffix && <span className="impact-metric-suffix">{suffix}</span>}
      </div>
      <div className="impact-metric-label">{label}</div>
      {context && <div className="impact-metric-context">{context}</div>}
    </div>
  );
}

function FlowStep({ label, done, active }) {
  return (
    <div
      className={`impact-flow-step${active ? ' is-active' : ''}${done ? ' is-done' : ''}`}
    >
      <span className="impact-flow-dot" />
      <span className="impact-flow-label">{label}</span>
    </div>
  );
}

export default function ImpactOrchestration() {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [activated, setActivated] = useState(false);   // strike fired → entry begins
  const [entryDone, setEntryDone] = useState(false);   // entry anim finished → evidence breathes
  const [storyIdx, setStoryIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const story = STORIES[storyIdx];

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Hero ↔ Panel causal link: panel appears BECAUSE statement became true.
  // 300ms after strike → entry begins (opacity+translateY), 700ms duration.
  useEffect(() => {
    const onStrike = () => {
      setTimeout(() => setActivated(true), 300);
    };
    window.addEventListener('granullo:strike-complete', onStrike);
    // Fallback for direct page entry (no hero sequence)
    const fallback = setTimeout(() => setActivated(true), 2600);
    return () => {
      window.removeEventListener('granullo:strike-complete', onStrike);
      clearTimeout(fallback);
    };
  }, []);

  // After entry animation settles → evidence starts breathing
  useEffect(() => {
    if (!activated) return;
    const t = setTimeout(() => setEntryDone(true), 950);
    return () => clearTimeout(t);
  }, [activated]);

  useEffect(() => {
    if (!mounted || paused) return;
    const t = setInterval(() => {
      setStoryIdx((i) => (i + 1) % STORIES.length);
    }, 7000);
    return () => clearInterval(t);
  }, [mounted, paused]);

  const inView = mounted;
  const countStart = entryDone;

  return (
    <motion.div
      ref={ref}
      className={`impact-panel${entryDone ? ' is-entry-done' : ''}`}
      data-testid="impact-orchestration"
      initial={{ opacity: 0, y: 12 }}
      animate={activated ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      transition={{
        opacity: { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] },
        y: { type: 'spring', stiffness: 82, damping: 22, mass: 0.95, restDelta: 0.001 },
      }}
    >
      {/* 1. Primary statement — "Real impact" draws in after panel arrives */}
      <h3 className="impact-statement">
        You helped prevent misuse.
        <br />
        <span className="impact-statement-soft">Real people.</span>{' '}
        <span className={`impact-statement-accent${entryDone ? ' is-drawn' : ''}`}>
          Real impact.
        </span>
      </h3>

      {/* 2. Metrics row */}
      <div className="impact-metrics-row">
        {METRICS.map((m, i) => (
          <MetricItem
            key={m.label}
            value={m.value}
            label={m.label}
            context={m.context}
            suffix={m.suffix}
            decimals={m.decimals || 0}
            start={countStart}
            delay={i * 0.14}
          />
        ))}
      </div>

      {/* 4. Latest impact story */}
      <div
        className="impact-story-wrap"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={storyIdx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.75, ease: [0.22, 0.61, 0.36, 1] }}
            className="impact-story"
            data-testid="impact-story"
          >
            <div
              className="impact-avatar"
              style={{ background: `linear-gradient(135deg, ${story.tone} 0%, ${story.tone}dd 100%)` }}
            >
              {story.initials}
            </div>
            <div className="impact-story-body">
              <p className="impact-story-context">
                {story.context} in {story.place} <span className="impact-story-flag">{story.flag}</span>
              </p>
              <blockquote className="impact-story-quote">
                &ldquo;{story.quote}&rdquo;
              </blockquote>
              <cite className="impact-story-cite">
                — {story.name}, {story.age} years old
              </cite>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Story pagination dots */}
        <div className="impact-story-dots" aria-hidden>
          {STORIES.map((_, i) => (
            <span
              key={i}
              className={`impact-story-dot${i === storyIdx ? ' is-active' : ''}`}
            />
          ))}
        </div>
      </div>

      {/* 5. Funds flow */}
      <div className={`impact-flow${entryDone ? ' is-live' : ''}`} data-testid="impact-flow">
        {FLOW.map((s, i) => (
          <React.Fragment key={s.label}>
            <FlowStep {...s} />
            {i < FLOW.length - 1 && <span className="impact-flow-connector" />}
          </React.Fragment>
        ))}
      </div>

      {/* 6. Closing line */}
      <p className="impact-closing">
        Money did what it was supposed to do.
      </p>

      {/* 7. Footer actions */}
      <div className="impact-footer">
        <a href="/impact" className="impact-footer-link" data-testid="impact-see-all">
          See all impact <span aria-hidden>→</span>
        </a>
        <a href="/share" className="impact-footer-link impact-footer-link--secondary" data-testid="impact-share">
          Share your impact
        </a>
      </div>

      <style>{impactStyles}</style>
    </motion.div>
  );
}

/* ═══ STYLES (Light + Dark) ═══ */
const impactStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap');

  .impact-panel {
    --scarlet: ${SCARLET};
    --ip-surface: #FFFFFF;
    --ip-surface-soft: #F7F6F2;
    --ip-text: #0A0A0A;
    --ip-text-soft: #2A2A2A;
    --ip-muted: #6B6B6B;
    --ip-muted-2: #9A9A9A;
    --ip-border: rgba(15, 15, 15, 0.09);
    --ip-border-soft: rgba(15, 15, 15, 0.06);
    --ip-divider: rgba(15, 15, 15, 0.07);

    font-family: var(--g-font-sans, system-ui, sans-serif);
    color: var(--ip-text);
  }

  [data-theme="dark"] .impact-panel {
    --ip-surface: #0F0F0F;
    --ip-surface-soft: #1A1A1A;
    --ip-text: #FDFBF6;
    --ip-text-soft: #DFDBD3;
    --ip-muted: #9F9B94;
    --ip-muted-2: #6A6660;
    --ip-border: rgba(255, 255, 255, 0.11);
    --ip-border-soft: rgba(255, 255, 255, 0.06);
    --ip-divider: rgba(255, 255, 255, 0.09);
  }

  /* ═══ Primary statement ═══ */
  .impact-statement {
    font-family: var(--g-font-serif, 'Charter', 'Iowan Old Style', Georgia, serif);
    font-size: 22px;
    line-height: 1.36;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--ip-text);
    margin: 0 0 30px 0;
  }
  .impact-statement-soft {
    color: var(--ip-text-soft);
    opacity: 0.92;
  }
  .impact-statement-accent {
    font-family: 'Kalam', 'Dancing Script', cursive;
    font-weight: 700;
    color: var(--scarlet);
    font-size: 1.42em;
    letter-spacing: 0;
    margin-left: 4px;
    display: inline-block;
    transform: translateY(3px);
    /* Initial state — invisible, clipped, slightly bled */
    clip-path: inset(0 100% -8% -3%);
    opacity: 0;
    filter: blur(0.5px);
    text-shadow: 0 0 4px rgba(205, 34, 0, 0.28);
    will-change: clip-path, transform, opacity, filter;
  }
  .impact-statement-accent.is-drawn {
    animation: impactAccentDrawIn 640ms forwards;
    animation-delay: 200ms;
  }
  /* Ink-like uneven draw — irregular keyframe spacing + trailing softness */
  @keyframes impactAccentDrawIn {
    0% {
      clip-path: inset(0 100% -8% -3%);
      opacity: 0;
      transform: translateY(3px) scale(0.985);
      filter: blur(0.5px);
      text-shadow: 0 0 4px rgba(205, 34, 0, 0.28);
      animation-timing-function: cubic-bezier(0.45, 0, 0.6, 0.2);
    }
    18% {
      clip-path: inset(0 82% -8% -3%);
      opacity: 0.55;
      text-shadow: 0 0 4px rgba(205, 34, 0, 0.32);
      animation-timing-function: cubic-bezier(0.2, 0.6, 0.3, 1);
    }
    44% {
      clip-path: inset(0 38% -8% -3%);
      opacity: 0.92;
      transform: translateY(3px) scale(1.008);
      filter: blur(0.35px);
      text-shadow: 0 0 3px rgba(205, 34, 0, 0.25);
      animation-timing-function: cubic-bezier(0.3, 0.5, 0.25, 1);
    }
    62% {
      clip-path: inset(0 18% -8% -3%);
      opacity: 1;
      transform: translateY(3px) scale(1.014);
      filter: blur(0.25px);
      text-shadow: 0 0 2.5px rgba(205, 34, 0, 0.2);
      animation-timing-function: cubic-bezier(0.4, 0.05, 0.25, 1);
    }
    82% {
      clip-path: inset(0 -2% -8% -3%);
      transform: translateY(3px) scale(1.004);
      filter: blur(0.12px);
      text-shadow: 0 0 1.5px rgba(205, 34, 0, 0.12);
      animation-timing-function: cubic-bezier(0.3, 0, 0.35, 1);
    }
    100% {
      clip-path: inset(0 -3% -8% -3%);
      opacity: 1;
      transform: translateY(3px) scale(1);
      filter: blur(0);
      text-shadow: none;
    }
  }

  /* ═══ Metrics row — evidence, not dashboard ═══ */
  .impact-metrics-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
    margin-bottom: 28px;
    padding-bottom: 24px;
    border-bottom: 1px solid var(--ip-divider);
  }
  .impact-metric {
    display: flex;
    flex-direction: column;
    gap: 3px;
    position: relative;
  }
  .impact-metric + .impact-metric::before {
    content: '';
    position: absolute;
    left: -14px;
    top: 6px;
    bottom: 8px;
    width: 1px;
    background: var(--ip-border-soft);
    opacity: 0.7;
  }
  .impact-metric-value {
    font-family: var(--g-font-serif, 'Charter', Georgia, serif);
    font-size: 26px;
    line-height: 1;
    font-weight: 500;
    color: var(--ip-text);
    letter-spacing: -0.018em;
    font-variant-numeric: tabular-nums;
  }
  .impact-metric-suffix {
    font-size: 15px;
    color: var(--ip-muted);
    margin-left: 1px;
    font-weight: 400;
    letter-spacing: 0;
  }
  .impact-metric-label {
    font-size: 11px;
    color: var(--ip-text-soft);
    letter-spacing: 0.01em;
    line-height: 1.3;
    font-weight: 500;
  }
  .impact-metric-context {
    font-size: 9.5px;
    color: var(--ip-muted);
    letter-spacing: 0.015em;
    line-height: 1.35;
    font-weight: 400;
    margin-top: 2px;
    opacity: 0.72;
  }

  /* ═══ Divider (between metrics & story) — replaced by metrics row border-bottom ═══ */
  .impact-divider {
    display: none;
  }

  /* ═══ Story — captured, not rendered ═══ */
  .impact-story-wrap {
    margin-bottom: 30px;
  }
  .impact-story {
    display: flex;
    gap: 18px;
    align-items: flex-start;
    padding: 22px 20px 22px 18px;
    background: var(--ip-surface-soft);
    border: 1px solid rgba(15, 15, 15, 0.035);
    border-radius: 14px;
    position: relative;
    transition: background 0.4s ease;
  }
  /* Ultra-subtle grain — captured, not rendered */
  .impact-story::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    opacity: 0.018;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    mix-blend-mode: multiply;
  }
  [data-theme="dark"] .impact-story {
    background: #1A1A1A;
    border-color: rgba(255, 255, 255, 0.05);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  }
  [data-theme="dark"] .impact-story::before {
    opacity: 0.028;
    mix-blend-mode: overlay;
  }
  .impact-story > * {
    position: relative;
    z-index: 1;
  }
  .impact-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--g-font-serif, Georgia, serif);
    font-size: 18px;
    font-weight: 500;
    color: #0F0F0F;
    flex-shrink: 0;
    box-shadow:
      inset 0 1px 2px rgba(255, 255, 255, 0.35),
      inset 0 -2px 4px rgba(0, 0, 0, 0.06),
      0 2px 6px rgba(0, 0, 0, 0.09);
    position: relative;
  }
  .impact-avatar::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 28%, rgba(255,255,255,0.28) 0%, transparent 55%);
    pointer-events: none;
  }
  .impact-story-body {
    flex: 1;
    min-width: 0;
  }
  .impact-story-context {
    font-size: 10.5px;
    color: var(--ip-muted);
    margin: 0 0 10px 0;
    letter-spacing: 0.02em;
    font-weight: 500;
    text-transform: uppercase;
    opacity: 0.88;
  }
  .impact-story-flag {
    margin-left: 3px;
    filter: saturate(0.95);
    text-transform: none;
  }
  .impact-story-quote {
    font-family: var(--g-font-serif, 'Charter', Georgia, serif);
    font-size: 15px;
    line-height: 1.6;
    color: var(--ip-text);
    font-style: italic;
    margin: 0 0 14px 0;
    padding: 0;
    border: none;
    letter-spacing: -0.008em;
    font-weight: 400;
  }
  .impact-story-cite {
    font-size: 12px;
    color: var(--ip-text-soft);
    font-style: normal;
    letter-spacing: 0.002em;
    font-weight: 500;
  }

  /* ═══ Story dots ═══ */
  .impact-story-dots {
    display: flex;
    justify-content: center;
    gap: 5px;
    margin-top: 10px;
  }
  .impact-story-dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--ip-border);
    transition: all 0.35s ease;
  }
  .impact-story-dot.is-active {
    background: var(--ip-text-soft);
    width: 14px;
    border-radius: 2px;
  }

  /* ═══ Flow — how money behaves here ═══ */
  .impact-flow {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 22px;
    padding: 0 2px;
  }
  .impact-flow-step {
    display: flex;
    align-items: center;
    gap: 7px;
    opacity: 0.55;
    transition: opacity 0.6s ease;
  }
  .impact-flow-step.is-done { opacity: 0.78; }
  .impact-flow-step.is-active { opacity: 1; }

  .impact-flow-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--ip-text-soft);
    transition: all 0.5s ease;
    flex-shrink: 0;
    opacity: 0.4;
  }
  .impact-flow-step.is-done .impact-flow-dot {
    opacity: 0.75;
  }
  .impact-flow-step.is-active .impact-flow-dot {
    background: var(--scarlet);
    opacity: 1;
    box-shadow: 0 0 0 3px rgba(205, 34, 0, 0.12);
  }
  /* Heartbeat — only after entry completes */
  .impact-flow.is-live .impact-flow-step.is-active .impact-flow-dot {
    animation: impactHeartbeat 4.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }

  @keyframes impactHeartbeat {
    0%, 100% { box-shadow: 0 0 0 3px rgba(205, 34, 0, 0.12); }
    50% { box-shadow: 0 0 0 6px rgba(205, 34, 0, 0.04); }
  }

  .impact-flow-label {
    font-size: 10.5px;
    color: var(--ip-muted);
    letter-spacing: 0.012em;
    white-space: nowrap;
    font-weight: 400;
  }
  .impact-flow-step.is-done .impact-flow-label {
    color: var(--ip-text-soft);
    font-weight: 400;
  }
  .impact-flow-step.is-active .impact-flow-label {
    color: var(--ip-text);
    font-weight: 500;
    letter-spacing: -0.002em;
  }

  .impact-flow-connector {
    flex: 1;
    height: 1px;
    background: var(--ip-divider);
    min-width: 8px;
    opacity: 0.55;
  }

  /* ═══ Closing — authored, not copy ═══ */
  .impact-closing {
    font-family: var(--g-font-serif, 'Charter', Georgia, serif);
    font-size: 14px;
    line-height: 1.55;
    color: var(--ip-text-soft);
    font-style: italic;
    margin: 0 0 22px 0;
    letter-spacing: 0.003em;
    opacity: 0.88;
  }

  /* ═══ Footer — grounded, intentional ═══ */
  .impact-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 18px;
    border-top: 1px solid var(--ip-border-soft);
  }
  .impact-footer-link {
    font-size: 11.5px;
    color: var(--ip-text);
    text-decoration: none;
    letter-spacing: 0.008em;
    font-weight: 500;
    transition: color 0.25s ease, opacity 0.25s ease;
    cursor: pointer;
    opacity: 0.88;
  }
  .impact-footer-link:hover {
    opacity: 1;
    color: var(--scarlet);
  }
  .impact-footer-link--secondary {
    color: var(--ip-text-soft);
    opacity: 0.7;
    font-weight: 500;
  }
  .impact-footer-link--secondary:hover {
    opacity: 1;
    color: var(--ip-text);
  }
`;
